import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import { Thread } from '../types';

interface UseInboxThreadsResult {
  threads: Thread[];
  loading: boolean;
  totalUnread: number;
}

/**
 * Real-time listener for thread summary docs — powers the participant inbox.
 * Returns threads ordered by most recent message first.
 */
export function useInboxThreads(
  participantId: string | null,
  organizationId: string | null,
): UseInboxThreadsResult {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!participantId || !organizationId) {
      setThreads([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const q = query(
      collection(db, 'threads'),
      where('participantId', '==', participantId),
      where('organizationId', '==', organizationId),
      orderBy('lastMessageAt', 'desc'),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Thread));
        setThreads(docs);
        setLoading(false);
      },
      (err) => console.error('[FS_DENY:useInboxThreads]', { participantId, organizationId, err }),
    );

    return () => unsubscribe();
  }, [participantId, organizationId]);

  const totalUnread = threads.reduce((sum, t) => sum + (t.participantUnread || 0), 0);

  return { threads, loading, totalUnread };
}
